import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "react-router";
import { marketFiltersToQuery, type MarketNav } from "../../shared/market";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { useCatalog } from "../lib/CatalogProvider";
import { imageUrl } from "../lib/api";
import { marketApi } from "../market/api";
import { useDropdowns } from "./useDropdowns";
import { useGame } from "../lib/GameProvider";

/** Category order in the "Comprar" panel (only those with cards on sale show). */
const NAV_CATEGORIES = ["singles", "booster-box", "etb", "sobres", "colecciones", "mazos"];
/** Where the menu is a bar with panels (styles.css); below it, the phone drawer. */
const DESKTOP = "(min-width: 900px)";

type Section = "buy" | "wanted" | "sell" | "help";
const SECTIONS: { id: Section; label: MessageKey; current: (path: string) => boolean }[] = [
  { id: "buy", label: "nav.shop", current: (p) => p.startsWith("/mercado") || p.startsWith("/producto/") || p === "/identificar" },
  { id: "wanted", label: "nav.wanted", current: (p) => p.startsWith("/se-busca") },
  { id: "sell", label: "nav.sell", current: (p) => ["/vender", "/vender/planes", "/vender/solicitar", "/compramos"].includes(p) },
  { id: "help", label: "nav.help", current: (p) => ["/envios", "/guia-de-compra", "/ayuda", "/reembolsos", "/arrepentimiento", "/contacto", "/nosotros"].includes(p) },
];

/**
 * The site's menu, by what people come to do (Comprar, Se busca, Vender, Ayuda). On wide screens each
 * item opens a panel under the bar; in the phone drawer each item unfolds in place (useDropdowns).
 */
export function NavMenu({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useI18n();
  const { pathname } = useLocation();
  const menu = useDropdowns<Section>(DESKTOP);
  const nav = useMarketNav(menu.open === "buy");
  const go = () => {
    menu.close();
    onNavigate();
  };

  return (
    <div className="nav-menu" ref={menu.rootRef}>
      {SECTIONS.map((s) => (
        <div key={s.id} className={menu.open === s.id ? "nav-item is-open" : "nav-item"} {...menu.itemProps(s.id)}>
          <button type="button" className={s.current(pathname) ? "nav-top is-current" : "nav-top"} {...menu.buttonProps(s.id)}>
            {t(s.label)}
            <span className="nav-caret" aria-hidden="true" />
          </button>
          {menu.open === s.id && (
            <div id={menu.panelId(s.id)} className="nav-panel">
              {s.id === "buy" && <BuyPanel nav={nav} onGo={go} />}
              {s.id === "wanted" && <WantedPanel onGo={go} />}
              {s.id === "sell" && <SellPanel onGo={go} />}
              {s.id === "help" && <HelpPanel onGo={go} />}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** What's on sale in the visitor's game, loaded the first time the panel opens (the header is on every page) and again on a switch. */
function useMarketNav(wanted: boolean): MarketNav | null {
  const { game } = useGame();
  const [nav, setNav] = useState<{ game: string; nav: MarketNav } | null>(null);
  const asked = useRef<string | null>(null);
  useEffect(() => {
    if (!wanted || asked.current === game) return;
    asked.current = game;
    // Without it the panel still leads to the whole market.
    marketApi.nav(game).then((n) => setNav({ game, nav: n }), () => {});
  }, [wanted, game]);
  return nav?.game === game ? nav.nav : null;
}

function PanelHead({ title, to, label, onGo }: { title: string; to?: string; label?: string; onGo: () => void }) {
  return (
    <div className="nav-panel-head">
      <strong>{title}</strong>
      {to && (
        <Link to={to} onClick={onGo} className="nav-panel-all">
          {label} →
        </Link>
      )}
    </div>
  );
}

function Column({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="nav-col">
      <h3 className="nav-col-title">{title}</h3>
      <ul>{children}</ul>
    </div>
  );
}

const setLink = (set: string) => `/mercado?${marketFiltersToQuery({ categories: ["singles"], sets: [set] })}`;

function BuyPanel({ nav, onGo }: { nav: MarketNav | null; onGo: () => void }) {
  const { t, pick } = useI18n();
  const { categories } = useCatalog().catalog;
  const offered = new Map(nav?.categories.map((c) => [c.slug, c.cards]) ?? []);
  const shown = [...NAV_CATEGORIES, ...categories.map((c) => c.slug).filter((s) => !NAV_CATEGORIES.includes(s))]
    .filter((slug) => offered.has(slug))
    .flatMap((slug) => categories.filter((c) => c.slug === slug));
  return (
    <>
      <PanelHead title={t("nav.shop")} to="/mercado" label={t("nav.allMarket")} onGo={onGo} />
      <div className="nav-panel-body">
        {shown.length > 0 && (
          <Column title={t("nav.categories")}>
            {shown.map((c) => (
              <li key={c.slug}>
                <Link to={`/mercado?${marketFiltersToQuery({ categories: [c.slug] })}`} onClick={onGo}>
                  {pick(c.name)} <span className="nav-count">{offered.get(c.slug)}</span>
                </Link>
              </li>
            ))}
          </Column>
        )}
        {nav && nav.sets.length > 0 && (
          <Column title={t("nav.sets")}>
            {nav.sets.map((s) => (
              <li key={s.name}>
                <Link to={setLink(s.name)} onClick={onGo}>
                  {s.name} <span className="nav-count">{s.cards}</span>
                </Link>
              </li>
            ))}
          </Column>
        )}
        <div className="nav-feature">
          {nav?.featured && (
            <Link to={setLink(nav.featured.set)} onClick={onGo} className="nav-feature-card">
              <img src={imageUrl(nav.featured.imageKey)} alt="" width={120} height={168} />
              <span>
                <strong>{nav.featured.set}</strong>
                <span className="muted small">{t("nav.setCards", { count: nav.featured.cards })}</span>
                <span className="btn btn-primary btn-sm">{t("nav.seeOffers")}</span>
              </span>
            </Link>
          )}
          <Link to="/identificar" onClick={onGo} className="btn btn-outline btn-sm nav-feature-btn">
            📷 {t("nav.identify")}
          </Link>
        </div>
      </div>
    </>
  );
}

function LinkList({ items, onGo }: { items: { to: string; label: MessageKey; hint?: MessageKey }[]; onGo: () => void }) {
  const { t } = useI18n();
  return (
    <ul className="nav-links">
      {items.map((i) => (
        <li key={i.to}>
          <Link to={i.to} onClick={onGo}>
            <strong>{t(i.label)}</strong>
            {i.hint && <span className="muted small">{t(i.hint)}</span>}
          </Link>
        </li>
      ))}
    </ul>
  );
}

function WantedPanel({ onGo }: { onGo: () => void }) {
  const { t } = useI18n();
  return (
    <>
      <PanelHead title={t("nav.wanted")} onGo={onGo} />
      <div className="nav-panel-body">
        <LinkList
          items={[
            { to: "/se-busca", label: "nav.wantedBoard", hint: "nav.wantedBoardHint" },
            { to: "/se-busca/publicar", label: "nav.wantedPost", hint: "nav.wantedPostHint" },
            { to: "/cuenta/compras?tab=busco", label: "nav.wants", hint: "nav.wantsHint" },
          ]}
          onGo={onGo}
        />
      </div>
    </>
  );
}

function SellPanel({ onGo }: { onGo: () => void }) {
  const { t } = useI18n();
  return (
    <>
      <PanelHead title={t("nav.sell")} onGo={onGo} />
      <div className="nav-panel-body">
        <LinkList
          items={[
            { to: "/vender", label: "nav.sellMarket", hint: "nav.sellMarketHint" },
            { to: "/mi-tienda", label: "nav.myShop", hint: "nav.myShopHint" },
            { to: "/vender/planes", label: "plans.nav", hint: "plans.navHint" },
            { to: "/compramos", label: "nav.weBuy", hint: "nav.weBuyHint" },
          ]}
          onGo={onGo}
        />
      </div>
    </>
  );
}

function HelpPanel({ onGo }: { onGo: () => void }) {
  const { t } = useI18n();
  return (
    <>
      <PanelHead title={t("nav.help")} onGo={onGo} />
      <div className="nav-panel-body">
        <LinkList
          items={[
            { to: "/guia-de-compra", label: "nav.guide" },
            { to: "/envios", label: "nav.shipping" },
            { to: "/ayuda", label: "nav.faq" },
            { to: "/reembolsos", label: "nav.refunds" },
            { to: "/arrepentimiento", label: "nav.withdrawal" },
            { to: "/contacto", label: "nav.contact" },
            { to: "/nosotros", label: "nav.about" },
          ]}
          onGo={onGo}
        />
      </div>
    </>
  );
}
